import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, AlertCircle, Eye, EyeOff, Lock } from 'lucide-react';

const UserAuthenticationLogin: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, user } = useAuth();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as any)?.from?.pathname || '/';

  useEffect(() => {
    if (user) {
      navigate(from, { replace: true });
    }
  }, [user, navigate, from]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const { error: signInError } = await signIn(email, password);

      if (signInError) {
        setError(signInError.message || 'Invalid email or password');
        setLoading(false);
        return;
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  const handleDemoLogin = async (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError('');
    setLoading(true);

    try {
      const { error: signInError } = await signIn(demoEmail, demoPassword);

      if (signInError) {
        setError(signInError.message || 'Invalid demo credentials');
        setLoading(false);
        return;
      }

      navigate(from, { replace: true });
    } catch (err: any) {
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      {/* Header with Logo */}
      <header className="border-b border-gray-200">
        <div className="px-6 py-4">
          <img
            src="/assets/images/evoqia-logo_-nb-1766401973047.png"
            alt="Evoqia logo with geometric green and gray icon element and modern typography"
            className="h-10 object-contain" />

        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="max-w-md w-full space-y-8">
          {/* Login Header */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-[#7CC363]/10 rounded-lg mb-4">
              <Lock className="h-6 w-6 text-[#7CC363]" />
            </div>
            <h1 className="text-3xl font-semibold text-gray-900 mb-2">Sign in to Evoqia</h1>
            <p className="text-gray-600">
              Access your quoting dashboard
            </p>
          </div>

          {/* Login Form Card */}
          <div className="bg-white rounded-lg border border-gray-200 p-8 space-y-6">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Error Message */}
              {error &&
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-start">
                    <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <p className="text-sm text-red-800">{error}</p>
                  </div>
                </div>
              }

              {/* Email Input */}
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                    placeholder="you@example.com"
                    disabled={loading} />

                </div>
              </div>

              {/* Password Input */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                    Password
                  </label>
                  <button
                    type="button"
                    onClick={() => navigate('/password-reset-request')}
                    className="text-sm text-[#7CC363] hover:text-[#6ab352] font-medium transition-colors"
                  >
                    Wachtwoord vergeten?
                  </button>
                </div>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    className="pl-10 pr-10 w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#7CC363] focus:border-transparent transition"
                    placeholder="••••••••"
                    disabled={loading} />

                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    disabled={loading}>

                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-[#7CC363] hover:bg-[#6AB352] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#7CC363] disabled:opacity-50 disabled:cursor-not-allowed transition">

                {loading ?
                <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Signing in...
                  </> :

                'Sign In'
                }
              </button>
            </form>
          </div>

          {/* Footer Links */}
          <div className="text-center text-sm text-gray-600">
            





          </div>
        </div>
      </main>
    </div>);

};

export default UserAuthenticationLogin;